/**
 * PebbleHighlightedArticle
 *
 * Drop-in React component for rendering article HTML with the cited paragraph
 * highlighted, based on a fragment value emitted by Pebble's explain API.
 *
 * ## How it pairs with the explain API
 *
 * Send a `source_url_format` containing the `text_highlight` template variable.
 * For an SPA where you handle `/articles/:uuid` routing yourself, use a plain
 * hash:
 *
 *   POST /api/v1/explain
 *   {
 *     ...,
 *     "source_url_format": "/articles/{{uuid}}#{{text_highlight}}"
 *   }
 *
 * The API responds with markdown links whose URLs already contain the
 * URL-encoded fragment, e.g.
 *
 *   [contract values growing 10%](/articles/8c81…#Contract%20values%20probably%20grew,up%2C%20the%20report%20said.)
 *
 * The fragment is either a single phrase or a `start,end` range form (see the
 * Chrome Text Fragments spec) — this component handles both.
 *
 * ## Usage
 *
 *   import { useLocation } from "react-router";
 *   import { PebbleHighlightedArticle } from "./PebbleHighlightedArticle";
 *
 *   function ArticlePage({ uuid }: { uuid: string }) {
 *     const { hash } = useLocation();
 *     const html = useArticleHtml(uuid);
 *
 *     return (
 *       <PebbleHighlightedArticle
 *         html={html}
 *         fragment={hash}
 *         highlightStyle={{ backgroundColor: "#fef08a" }}
 *       />
 *     );
 *   }
 *
 * ## Notes
 *
 * - The browser strips `:~:text=…` from the URL before exposing it via
 *   `window.location.hash`, so the spec form is unreadable from `location.hash`
 *   in an SPA. It *does* survive when the URL is read from another source —
 *   e.g. a markdown link's `href` attribute or a string prop. Recommendation:
 *   use plain `#{{text_highlight}}` for SPA route hashes; use
 *   `#:~:text={{text_highlight}}` if you want the same URL to also fire
 *   native browser highlighting on full-page navigations. The component
 *   accepts both.
 * - The highlight is re-applied whenever `html` or `fragment` change.
 * - If no match is found in the rendered HTML, the article displays normally
 *   (no error, no console noise).
 * - The component preserves the host React tree — DOM mutations are scoped to
 *   the rendered article container and are reverted on cleanup.
 * - Highlights are inline `<mark>` elements styled via inline `style` (so they
 *   beat ordinary stylesheet rules). If your CSS resets `mark` with
 *   `!important`, the inline style loses; pass a `highlightClassName` and
 *   style it directly, or remove the offending reset.
 */

import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";

export type PebbleHighlightedArticleProps = {
  /** Article HTML to render. Mutually exclusive with `children`. */
  html?: string;

  /**
   * React children to render inside the highlight container. Use this when the
   * content is composed of multiple React elements (e.g. a headline `<Text>`
   * plus a body `<div>`) and you want a single Range to span across them so a
   * `start,end` fragment highlights the in-between text continuously.
   */
  children?: ReactNode;

  /**
   * Text fragment to highlight. Accepts:
   *   - `start,end` (URL-encoded; the comma separates start and end phrases)
   *   - A single phrase (URL-encoded)
   *   - The same with a leading `#`
   *   - The spec form `#:~:text=…` (matches if not already stripped by the browser)
   *
   * Pass `useLocation().hash` directly when using React Router.
   */
  fragment?: string | null;

  /** Inline styles applied to the highlight. Defaults to a yellow background. */
  highlightStyle?: CSSProperties;

  /** Class name applied to each highlight element. Default: `pebble-highlight`. */
  highlightClassName?: string;

  /** Scroll the first highlight into view after applying. Default: `true`. */
  scrollIntoView?: boolean;

  /** Class name on the article container. */
  className?: string;

  /** Inline style on the article container. */
  style?: CSSProperties;
};

const DEFAULT_HIGHLIGHT_STYLE: CSSProperties = {
  backgroundColor: "#fef08a",
  color: "inherit",
};

export function PebbleHighlightedArticle({
  html,
  children,
  fragment,
  highlightStyle = DEFAULT_HIGHLIGHT_STYLE,
  highlightClassName = "pebble-highlight",
  scrollIntoView = true,
  className,
  style,
}: PebbleHighlightedArticleProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Stabilize the style key so a fresh `{{ … }}` literal each render doesn't
  // thrash the effect's deps and force unwrap/re-wrap on every parent render.
  const styleKey = JSON.stringify(highlightStyle ?? null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !fragment) return;

    return applyTextFragmentHighlight(container, fragment, {
      highlightStyle,
      highlightClassName,
      scrollIntoView,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html, fragment, styleKey, highlightClassName, scrollIntoView]);

  if (children !== undefined) {
    return (
      <div ref={containerRef} className={className} style={style}>
        {children}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={className}
      style={style}
      dangerouslySetInnerHTML={{ __html: html ?? "" }}
    />
  );
}

// ---------- internals ----------

type HighlightOptions = {
  highlightStyle?: CSSProperties;
  highlightClassName?: string;
  scrollIntoView?: boolean;
};

const noop = () => {};

function applyTextFragmentHighlight(
  container: HTMLElement,
  fragment: string,
  opts: HighlightOptions,
): () => void {
  const parsed = parseTextFragment(fragment);
  if (!parsed) return noop;

  const range = findTextRange(container, parsed.start, parsed.end);
  if (!range) return noop;

  const marks = wrapRange(range, opts.highlightStyle, opts.highlightClassName);
  if (marks.length === 0) return noop;

  if (opts.scrollIntoView) {
    const reduceMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    marks[0].scrollIntoView({
      block: "center",
      behavior: reduceMotion ? "auto" : "smooth",
    });
  }

  return () => {
    for (const mark of marks) {
      const parent = mark.parentNode;
      if (!parent) continue;
      while (mark.firstChild) {
        parent.insertBefore(mark.firstChild, mark);
      }
      parent.removeChild(mark);
    }
    container.normalize();
  };
}

export function parseTextFragment(
  raw: string,
): { start: string; end?: string } | null {
  let body = raw.trim();
  if (!body) return null;

  if (body.startsWith("#")) body = body.slice(1);
  if (body.startsWith(":~:text=")) body = body.slice(":~:text=".length);
  if (!body) return null;

  // Split on the first unencoded comma — encoded commas inside the start/end
  // phrases are %2C, so the first literal `,` is always the separator.
  const commaIdx = body.indexOf(",");
  if (commaIdx === -1) {
    const start = safeDecode(body);
    return start ? { start } : null;
  }

  const start = safeDecode(body.slice(0, commaIdx));
  const end = safeDecode(body.slice(commaIdx + 1));
  if (!start) return null;
  return end ? { start, end } : { start };
}

function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/**
 * Walks `container`'s text nodes, builds a flat string with offset map, and
 * locates a Range matching `start` (and optionally extending to `end`).
 *
 * Supports matches that span block-level elements — the common case for the
 * `start,end` form, where the start phrase lives at the end of one `<p>` and
 * the end phrase lives partway through a later `<p>`.
 *
 * Match tolerance: NBSPs / narrow no-break spaces collapse to a regular
 * space, runs of whitespace collapse to a single space, and curly/smart
 * quotes fold to straight quotes. Comparison is case-insensitive. The
 * resulting offsets are mapped back to the *original* flat text so the
 * Range covers exactly the matched characters in the DOM.
 */
function findTextRange(
  container: HTMLElement,
  start: string,
  end: string | undefined,
): Range | null {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  const segments: string[] = [];

  let current = walker.nextNode() as Text | null;
  while (current) {
    nodes.push(current);
    segments.push(current.data);
    current = walker.nextNode() as Text | null;
  }

  const plan = planHighlight(segments, start, end);
  if (!plan) return null;

  const range = document.createRange();
  range.setStart(nodes[plan.startSegment], plan.startOffset);
  range.setEnd(nodes[plan.endSegment], plan.endOffset);
  return range;
}

export type HighlightPlan = {
  startSegment: number;
  startOffset: number;
  endSegment: number;
  endOffset: number;
};

export function planHighlight(
  segments: string[],
  start: string,
  end: string | undefined,
): HighlightPlan | null {
  let flat = "";
  const segmentStarts: number[] = [];
  for (const seg of segments) {
    segmentStarts.push(flat.length);
    flat += seg;
  }

  const { norm: flatNorm, map: origMap } = normalizeWithMap(flat);
  const startNorm = normalizeQuery(start);
  if (!startNorm) return null;

  const startIdxNorm = flatNorm.indexOf(startNorm);

  let highlightStartNorm: number;
  let highlightEndNorm: number;

  if (startIdxNorm !== -1) {
    highlightStartNorm = startIdxNorm;
    highlightEndNorm = startIdxNorm + startNorm.length;
    if (end) {
      const endNorm = normalizeQuery(end);
      const endStartNorm = endNorm
        ? flatNorm.indexOf(endNorm, highlightEndNorm)
        : -1;
      // If the end phrase isn't found, fall back to highlighting just the start
      // phrase. Better than nothing.
      if (endStartNorm !== -1) {
        highlightEndNorm = endStartNorm + endNorm.length;
      }
    }
  } else if (end) {
    // Start phrase missing — common when the citation's start lives in a
    // sibling element outside the searched container (e.g. a popover renders
    // the article body but not the headline). Fall back to highlighting just
    // the end phrase if it's present.
    const endNorm = normalizeQuery(end);
    if (!endNorm) return null;
    const endStartNorm = flatNorm.indexOf(endNorm);
    if (endStartNorm === -1) return null;
    highlightStartNorm = endStartNorm;
    highlightEndNorm = endStartNorm + endNorm.length;
  } else {
    return null;
  }

  // Map normalized offsets back to original flat-text offsets. `origMap[i]`
  // is the original index of `flatNorm[i]`; the past-the-end case maps to the
  // flat string length so the exclusive endpoint is uniform.
  const startIdx = origMap[highlightStartNorm];
  const endIdx =
    highlightEndNorm < origMap.length ? origMap[highlightEndNorm] : flat.length;

  const startPos = locateInSegments(segmentStarts, startIdx);
  const endPos = locateInSegments(segmentStarts, endIdx);
  if (!startPos || !endPos) return null;

  return {
    startSegment: startPos.segmentIdx,
    startOffset: startPos.offset,
    endSegment: endPos.segmentIdx,
    endOffset: endPos.offset,
  };
}

function locateInSegments(
  segmentStarts: number[],
  flatIdx: number,
): { segmentIdx: number; offset: number } | null {
  for (let i = segmentStarts.length - 1; i >= 0; i--) {
    if (flatIdx >= segmentStarts[i]) {
      return { segmentIdx: i, offset: flatIdx - segmentStarts[i] };
    }
  }
  return null;
}

/** Build `flatNorm` plus a parallel `map` such that `flat[map[i]]` is the
 * original character that produced `flatNorm[i]`. Multiple whitespace chars
 * collapse to a single space (only the first contributes to `map`). Smart
 * quotes fold to straight; everything else lowercases. */
function normalizeWithMap(s: string): { norm: string; map: number[] } {
  let norm = "";
  const map: number[] = [];
  let prevWasSpace = false;
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (isCollapsibleSpace(c)) {
      if (prevWasSpace) continue;
      norm += " ";
      map.push(i);
      prevWasSpace = true;
    } else {
      norm += foldChar(c);
      map.push(i);
      prevWasSpace = false;
    }
  }
  return { norm, map };
}

function normalizeQuery(s: string): string {
  let out = "";
  let prevWasSpace = true; // collapses leading whitespace too
  for (const c of s) {
    if (isCollapsibleSpace(c)) {
      if (prevWasSpace) continue;
      out += " ";
      prevWasSpace = true;
    } else {
      out += foldChar(c);
      prevWasSpace = false;
    }
  }
  return out.replace(/\s+$/, "");
}

function isCollapsibleSpace(c: string): boolean {
  // Regular whitespace plus NBSP (U+00A0) and narrow no-break space (U+202F),
  // which `\s` does include in modern engines but we keep explicit for clarity.
  return c === " " || c === " " || /\s/.test(c);
}

function foldChar(c: string): string {
  // Curly quotes → straight; everything else lowercase.
  if (c === "‘" || c === "’" || c === "‚" || c === "‛") return "'";
  if (c === "“" || c === "”" || c === "„" || c === "‟") return '"';
  return c.toLowerCase();
}

/**
 * Wraps each text node segment intersecting `range` in a `<mark>` element.
 *
 * Multiple `<mark>`s are produced when the range crosses block-level
 * boundaries (each `<p>`'s text gets its own mark). Browsers render adjacent
 * marks as one continuous visual highlight.
 */
function wrapRange(
  range: Range,
  highlightStyle: CSSProperties | undefined,
  highlightClassName: string | undefined,
): HTMLElement[] {
  // If the range is entirely within one text node, `commonAncestorContainer`
  // IS that text node — and a TreeWalker rooted on a text node yields nothing
  // (text nodes have no descendants). Walk from the parent in that case.
  const ancestor = range.commonAncestorContainer;
  const root =
    ancestor.nodeType === Node.TEXT_NODE && ancestor.parentNode
      ? ancestor.parentNode
      : ancestor;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      return range.intersectsNode(node)
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT;
    },
  });

  const intersecting: Text[] = [];
  let current = walker.nextNode() as Text | null;
  while (current) {
    intersecting.push(current);
    current = walker.nextNode() as Text | null;
  }

  const marks: HTMLElement[] = [];

  for (const node of intersecting) {
    const isStart = node === range.startContainer;
    const isEnd = node === range.endContainer;
    const startOffset = isStart ? range.startOffset : 0;
    const endOffset = isEnd ? range.endOffset : node.length;
    if (startOffset >= endOffset) continue;

    let target = node;
    if (endOffset < target.length) {
      target.splitText(endOffset);
    }
    if (startOffset > 0) {
      target = target.splitText(startOffset);
    }

    const mark = document.createElement("mark");
    if (highlightClassName) mark.className = highlightClassName;
    if (highlightStyle) applyStyle(mark, highlightStyle);

    target.parentNode!.insertBefore(mark, target);
    mark.appendChild(target);
    marks.push(mark);
  }

  return marks;
}

function applyStyle(el: HTMLElement, style: CSSProperties): void {
  for (const [key, value] of Object.entries(style)) {
    if (value == null) continue;
    // CSSStyleDeclaration accepts camelCase keys via index access at runtime.
    (el.style as unknown as Record<string, string>)[key] = String(value);
  }
}
